import { useMemo } from "react";
import { extractProjectIds } from "./jwtClaims";
import { useAuth } from "../auth/AuthContext";
import { type Identity } from "../identity/identityClient";
import { useIdentity } from "../identity/useIdentity";

export interface AuthorizedProject {
    id: string;
    name: string;
}

export interface UseAuthorizedProjectsResult {
    /**
     * Intersection of (a) the JWT `projectIds` claim and
     * (b) `/identity.projects[].id`. Anything in only one source is excluded.
     * Sorted alphabetically by name (locale-aware).
     */
    authorizedProjects: AuthorizedProject[];
    /** True while either JWT or identity is in a non-ready state. */
    isLoading: boolean;
}

export function useAuthorizedProjects(): UseAuthorizedProjectsResult {
    const { jwt } = useAuth();
    const { identity, status } = useIdentity();

    const authorizedProjects = useMemo(() => {
        if (jwt === null) return [];
        if (status !== "ready" || !identity) return [];
        return computeIntersection(jwt, identity);
    }, [jwt, identity, status]);

    const isLoading = jwt !== null && status !== "ready" && status !== "error";

    return { authorizedProjects, isLoading };
}

function computeIntersection(jwt: string, identity: Identity): AuthorizedProject[] {
    const claimIds = new Set(extractProjectIds(jwt));
    const intersection: AuthorizedProject[] = [];
    for (const project of identity.projects) {
        if (claimIds.has(project.id)) {
            intersection.push({ id: project.id, name: project.name });
        }
    }
    return intersection.sort((a, b) => a.name.localeCompare(b.name));
}
